/**
 * Level loader — parses JSON level data into runtime format.
 *
 * Handles fetching level JSON (from bundled imports or network),
 * validation, and conversion to the runtime obstacle/terrain format
 * used by the game controller.
 */

import { hexToColor, type Color } from '@/types';
import type { LevelData, RuntimeLevel, LevelSegment, ObstacleDef } from './level';
import {
  type Obstacle, type PadSize, type OrbSize,
  JUMPPAD_DEFAULT_SIZES, JUMPPAD_TIER_COLORS,
  ORB_DEFAULT_DIAMETERS, ORB_TIER_COLORS,
} from './obstacles';

/** Load a level from a JSON object (for bundled levels) */
export function loadLevel(data: LevelData): RuntimeLevel {
  // Compute segment offsets (cumulative x position where each segment starts)
  const segmentOffsets: number[] = [];
  let offset = 0;
  for (const seg of data.segments) {
    segmentOffsets.push(offset);
    offset += seg.length;
  }

  return {
    data,
    totalLength: offset,
    segmentOffsets,
    baseColor: hexToColor(data.settings.baseColor),
    accentColor: hexToColor(data.settings.accentColor),
    backgroundColor: hexToColor(data.settings.backgroundColor),
  };
}

/** Convert level segment obstacles into runtime Obstacle instances */
export function buildObstacles(
  segments: LevelSegment[],
  segmentOffsets: number[],
  spikeColor: Color,
  wallColor: Color,
): Obstacle[] {
  const obstacles: Obstacle[] = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const baseX = segmentOffsets[i];

    for (const def of seg.obstacles) {
      obstacles.push(obstacleFromDef(def, baseX, spikeColor, wallColor));
    }
  }

  return obstacles;
}

function obstacleFromDef(
  def: ObstacleDef,
  segmentBaseX: number,
  spikeColor: Color,
  wallColor: Color,
): Obstacle {
  const worldX = segmentBaseX + def.position.along;

  if (def.type === 'jumppad') {
    const padSize: PadSize = def.visual?.padSize ?? 'medium';
    const defaults = JUMPPAD_DEFAULT_SIZES[padSize];
    const width = def.size.width > 0 ? def.size.width : defaults.width;
    const height = def.size.height > 0 ? def.size.height : defaults.height;
    const color = def.visual?.color
      ? hexToColor(def.visual.color)
      : { ...JUMPPAD_TIER_COLORS[padSize] };
    return {
      type: 'jumppad',
      x: worldX,
      y: def.position.up,
      width, height,
      color,
      angle: 0,
      baseX: worldX,
      baseY: def.position.up,
      behavior: null,
      time: 0,
      padSize,
      padCooldown: 0,
      firedTimer: 0,
    };
  }

  if (def.type === 'orb') {
    const orbSize: OrbSize = def.visual?.orbSize ?? 'medium';
    const defaultDiameter = ORB_DEFAULT_DIAMETERS[orbSize];
    // Orbs are circles — diameter is taken from `width` (the authored value).
    // Any non-zero `height` is ignored so orbs stay round even if a level
    // editor writes a stale height value.
    const diameter = def.size.width > 0 ? def.size.width : defaultDiameter;
    const color = def.visual?.color
      ? hexToColor(def.visual.color)
      : { ...ORB_TIER_COLORS[orbSize] };
    return {
      type: 'orb',
      x: worldX,
      y: def.position.up,
      width: diameter, height: diameter,
      color,
      angle: 0,
      baseX: worldX,
      baseY: def.position.up,
      behavior: null,
      time: 0,
      orbSize,
      firedTimer: 0,
    };
  }

  const baseColor = def.type === 'spike' ? spikeColor : wallColor;
  const color = def.visual?.color ? hexToColor(def.visual.color) : baseColor;

  return {
    type: def.type,
    x: worldX,
    y: def.position.up, // 'up' is ground-relative offset; game.ts adds groundY
    width: def.size.width,
    height: def.size.height,
    color,
    angle: 0,
    // Dynamic obstacle properties
    baseX: worldX,
    baseY: def.position.up,
    behavior: def.behavior ?? null,
    time: 0,
  };
}

/** Build runtime collectibles from level data */
export function buildCollectibles(
  data: LevelData,
  segmentOffsets: number[],
): RuntimeCollectible[] {
  return data.collectibles.map((c) => {
    // Find which segment the 'along' position belongs to
    // Collectible positions are absolute in the level
    return {
      type: c.type,
      x: c.position.along,
      y: c.position.up,
      index: c.index ?? 0,
      powerupType: c.powerupType,
      collected: false,
      bobPhase: Math.random() * Math.PI * 2,
    };
  });
}

export interface RuntimeCollectible {
  type: 'star' | 'fragment' | 'powerup';
  x: number;
  y: number;
  index: number;
  powerupType?: string;
  collected: boolean;
  bobPhase: number;
}

/** Build runtime triggers from level data */
export function buildTriggers(data: LevelData): RuntimeTrigger[] {
  return data.triggers.map((t) => ({
    ...t,
    fired: false,
    progress: 0,
  }));
}

export interface RuntimeTrigger {
  id: string;
  type: string;
  position: number;
  duration: number;
  easing: string;
  params: Record<string, unknown>;
  fired: boolean;
  progress: number;
}
